import { errorMessage } from 'components/common/Form/ErrorMessages';
import Joi from 'joi';
import { FieldValues, UseFormTrigger } from 'react-hook-form';

export const EXAMINATION_STATUSES = ['FIRST', 'SECOND', 'LAST'] as const;

export const EXAMINATION_MONTH_OPTIONS: { label: string; value: number }[] = [
  { label: 'Januar', value: 1 },
  { label: 'Februar', value: 2 },
  { label: 'März', value: 3 },
  { label: 'April', value: 4 },
  { label: 'Mai', value: 5 },
  { label: 'Juni', value: 6 },
  { label: 'Juli', value: 7 },
  { label: 'August', value: 8 },
  { label: 'September', value: 9 },
  { label: 'Oktober', value: 10 },
  { label: 'November', value: 11 },
  { label: 'Dezember', value: 12 },
];

export type ExaminationEligibleStatus = (typeof EXAMINATION_STATUSES)[number];

export type ExaminationDateFormValues = {
  educationStatus?: string | null;
  examinationDateUnknown?: boolean;
  examinationMonth?: number | null;
  examinationYear?: number | null;
};

type ExaminationDateInput = {
  month?: number | string | null;
  year?: number | string | null;
  educationStatus?: string | null;
  now?: Date;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MS_PER_WEEK = 7 * MS_PER_DAY;

const addDays = (date: Date, days: number): Date => new Date(date.getTime() + days * MS_PER_DAY);

const addWeeks = (date: Date, weeks: number): Date =>
  new Date(date.getTime() + weeks * MS_PER_WEEK);

/**
 * Validates exam month/year against Lehrdrittel windows.
 * Uses the 1st of the selected month as the exam date for distance math.
 *
 * - FIRST:  > 110 weeks from now
 * - SECOND: > 405 days and < 771 days from now
 * - LAST:   in the future and < 58 weeks from now
 */
export const isExaminationDateInRange = ({
  month,
  year,
  educationStatus,
  now = new Date(),
}: ExaminationDateInput): boolean => {
  if (month == null || month === '' || year == null || year === '') return true;

  const monthValue = typeof month === 'string' ? Number(month) : month;
  const yearValue = typeof year === 'string' ? Number(year) : year;

  if (Number.isNaN(monthValue) || Number.isNaN(yearValue)) return false;
  if (monthValue < 1 || monthValue > 12) return false;

  if (
    !educationStatus ||
    !EXAMINATION_STATUSES.includes(educationStatus as ExaminationEligibleStatus)
  ) {
    return true;
  }

  const examDate = new Date(yearValue, monthValue - 1, 1);
  examDate.setHours(0, 0, 0, 0);

  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  switch (educationStatus) {
    case 'FIRST':
      return examDate.getTime() > addWeeks(today, 110).getTime();
    case 'SECOND':
      return (
        examDate.getTime() > addDays(today, 405).getTime() &&
        examDate.getTime() < addDays(today, 771).getTime()
      );
    case 'LAST':
      return (
        examDate.getTime() > today.getTime() && examDate.getTime() < addWeeks(today, 58).getTime()
      );
    default:
      return true;
  }
};

export const examinationDateUnknownSchema = Joi.boolean().default(false);

export const examinationMonthSchema = Joi.when('examinationDateUnknown', {
  is: true,
  then: Joi.optional().allow(null, ''),
  otherwise: Joi.when('educationStatus', {
    is: Joi.valid(...EXAMINATION_STATUSES),
    then: Joi.number().required().min(1).max(12).messages({
      'number.base': errorMessage.empty,
      'any.required': errorMessage.empty,
      'number.min': errorMessage.empty,
      'number.max': errorMessage.empty,
    }),
    otherwise: Joi.optional().allow(null, ''),
  }),
});

/**
 * Joi custom for examinationYear: checks month + year + Lehrdrittel together.
 *
 * Lives on this field (not on the parent object) so the rule still runs when other
 * fields are invalid — e.g. empty school name. Object-level `.custom()` is skipped
 * in that case.
 */
const validateExaminationYearAgainstStage = (
  year: number,
  helpers: Joi.CustomHelpers
): number | Joi.ErrorReport => {
  const formValues = helpers.state.ancestors[0] as ExaminationDateFormValues;

  if (formValues.examinationDateUnknown) {
    return year;
  }

  const isValid = isExaminationDateInRange({
    month: formValues.examinationMonth,
    year,
    educationStatus: formValues.educationStatus,
  });

  if (!isValid) {
    return helpers.message({ custom: errorMessage.examinationDateRange });
  }

  return year;
};

export const examinationYearSchema = Joi.when('examinationDateUnknown', {
  is: true,
  then: Joi.optional().allow(null, ''),
  otherwise: Joi.when('educationStatus', {
    is: Joi.valid(...EXAMINATION_STATUSES),
    then: Joi.number()
      .required()
      .min(2000)
      .max(2999)
      .messages({
        'string.empty': errorMessage.empty,
        'number.min': errorMessage.year,
        'number.max': errorMessage.year,
        'number.base': errorMessage.year,
        'number.empty': errorMessage.year,
      })
      .custom(validateExaminationYearAgainstStage, 'Lehrdrittel exam date window'),
    otherwise: Joi.optional().allow(null, ''),
  }),
});

export const PREFILLED_EXAMINATION_DATE_FIELD_NAMES = [
  'examinationMonth',
  'examinationYear',
] as const;

/** Whether prefilled exam date fields should be validated on form mount. */
export const shouldValidatePrefilledExaminationDate = (
  values: ExaminationDateFormValues
): boolean => {
  if (values.examinationDateUnknown) {
    return false;
  }

  if (
    !values.educationStatus ||
    !EXAMINATION_STATUSES.includes(values.educationStatus as ExaminationEligibleStatus)
  ) {
    return false;
  }

  const hasMonth = values.examinationMonth != null;
  const hasYear = values.examinationYear != null;

  return hasMonth || hasYear;
};

export const getPrefilledExaminationDateKey = (values: ExaminationDateFormValues): string =>
  [
    values.educationStatus ?? '',
    values.examinationDateUnknown ? '1' : '0',
    values.examinationMonth ?? '',
    values.examinationYear ?? '',
  ].join(':');

/**
 * Revalidates exam month + year together.
 * Needed because the Lehrdrittel range custom lives on `examinationYear` only —
 * a month-only trigger leaves year range errors stale.
 */
export const triggerExaminationDateFields = async <T extends FieldValues>(
  trigger: UseFormTrigger<T>
): Promise<void> => {
  await trigger([...PREFILLED_EXAMINATION_DATE_FIELD_NAMES] as Parameters<UseFormTrigger<T>>[0]);
};

/** Triggers Joi validation for prefilled exam month/year (e.g. on form mount). */
export const validatePrefilledExaminationDateFields = async <T extends FieldValues>(
  values: ExaminationDateFormValues,
  trigger: UseFormTrigger<T>
): Promise<void> => {
  if (!shouldValidatePrefilledExaminationDate(values)) {
    return;
  }

  await triggerExaminationDateFields(trigger);
};
